import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { KeyRound, Pencil, Save, Send, Settings2, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/feedback/EmptyState";
import { toast } from "@/components/feedback/ToastHost";
import { useConfirm } from "@/components/feedback/ConfirmDialog";
import { errorMessage } from "@/lib/errors";
import { isDeletedRecord, useAuditHighlight } from "@/lib/auditHighlight";
import { AuditGhostRecord } from "@/components/admin/AuditGhostRecord";
import { TranslateFieldButton } from "@/components/i18n/TranslateFieldButton";
import { TranslateSectionButton } from "@/components/i18n/TranslateSectionButton";
import { useLanguages } from "@/hooks/useLanguages";
import {
  useMachineTranslateField,
  useMachineTranslateSection,
  usePublishTranslations,
  useTranslations,
  useUpsertTranslation,
} from "@/hooks/useTranslationsAdmin";
import {
  createTranslationKey,
  deleteTranslationKey,
  listTranslationNamespaces,
  updateTranslationKey,
} from "@/services/api/i18n";
import type { TranslationRow } from "@/services/api/types";

const PAGE_SIZE = 25;

function ValueCell({
  row,
  locale,
  primary,
  primaryCode,
}: {
  row: TranslationRow;
  locale: string;
  primary: boolean;
  primaryCode: string;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(row.values[locale] ?? "");
  const upsert = useUpsertTranslation();
  const machine = useMachineTranslateField();

  const sourceValue = primary ? value : (row.values[primaryCode] ?? "");

  const commit = () => {
    if (value === (row.values[locale] ?? "")) return;
    upsert.mutate(
      { key: row.key, language_code: locale, value },
      {
        onSuccess: () => toast.success(t("translations.valueSaved")),
        onError: (error) => toast.error(errorMessage(error)),
      },
    );
  };

  const translate = () => {
    machine.mutate(
      { key: row.key, target_language: locale },
      {
        onSuccess: (result) => {
          setValue(result.value);
          toast.success(t("translations.translated"));
        },
        onError: (error) => toast.error(errorMessage(error)),
      },
    );
  };

  const input = (
    <Input
      value={value}
      onChange={(event) => setValue(event.target.value)}
      onBlur={() => commit()}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          commit();
          (event.target as HTMLInputElement).blur();
        }
      }}
      className={primary ? "" : "pe-9"}
      placeholder={primary ? t("translations.sourceText") : undefined}
    />
  );

  return (
    <div className="min-w-52">
      {primary ? (
        input
      ) : (
        <TranslateFieldButton
          onTranslate={translate}
          enabled={Boolean(sourceValue.trim())}
          busy={machine.isPending}
        >
          {input}
        </TranslateFieldButton>
      )}
      {row.machine_translated[locale] && !primary ? (
        <p className="mt-0.5 text-[10px] text-muted-foreground">{t("translations.machineFlag")}</p>
      ) : null}
    </div>
  );
}

/** The paginated, searchable translation workbench. */
export function TranslationsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Audit deep-link context (?r=<id>&a=<action>&f=<fields>&ar=<auditId>):
  // the linked key's row carries a thick yellow border; a deleted key is
  // rendered as a ghost copy above the table.
  const audit = useAuditHighlight();
  const ghostRecord = isDeletedRecord(audit);
  const highlight = audit.recordId;
  const languages = useLanguages();

  const [q, setQ] = useState("");
  const [namespace, setNamespace] = useState<string | undefined>();
  const [machineOnly, setMachineOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [committedQ, setCommittedQ] = useState("");
  const [keyDialog, setKeyDialog] = useState<
    | { mode: "create" }
    | { mode: "edit"; row: TranslationRow }
    | null
  >(null);
  const [keyForm, setKeyForm] = useState({ key: "", namespace: "nav", description: "" });

  const active = languages.data?.filter((language) => language.is_active) ?? [];
  const primaryCode = active.find((language) => language.is_primary)?.code ?? "en";

  const query = useTranslations({
    q: committedQ || undefined,
    namespace,
    machine_only: machineOnly,
    limit: PAGE_SIZE,
    offset,
  });

  const allNamespaces = useQuery({
    queryKey: ["translation-namespaces"],
    queryFn: () => listTranslationNamespaces(),
  });

  const sectionTranslate = useMachineTranslateSection();
  const publish = usePublishTranslations();

  const visibleNamespaces = useMemo(
    () =>
      Array.from(
        new Set([
          ...(allNamespaces.data ?? []),
          ...(query.data?.items ?? []).map((row) => row.namespace),
        ]),
      ).sort(),
    [allNamespaces.data, query.data],
  );

  const refreshKeys = () => {
    void queryClient.invalidateQueries({ queryKey: ["translations"] });
    void queryClient.invalidateQueries({ queryKey: ["translation-namespaces"] });
  };

  const saveKey = async () => {
    // Namespace is always prefixed to the key; correct hand-entered values.
    const prefix = `${keyForm.namespace}.`;
    const raw = keyForm.key.trim();
    const key = raw.startsWith(prefix) ? raw : `${keyForm.namespace}.${raw.replace(/^[a-zA-Z0-9._-]*\./, "")}`;
    try {
      if (keyDialog?.mode === "edit") {
        await updateTranslationKey(keyDialog.row.key, {
          key,
          namespace: keyForm.namespace,
          description: keyForm.description || null,
        });
      } else {
        await createTranslationKey({
          key,
          namespace: keyForm.namespace,
          description: keyForm.description || null,
        });
      }
      toast.success(t("translations.valueSaved"));
      setKeyDialog(null);
      refreshKeys();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const removeKey = useMutation({
    mutationFn: (key: string) => deleteTranslationKey(key),
    onSuccess: () => {
      toast.success(t("admin.deleted"));
      setKeyDialog(null);
      refreshKeys();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const confirm = useConfirm();
  const confirmDeleteKey = async (row: TranslationRow) => {
    const ok = await confirm({
      title: t("admin.deleteRecord", { name: row.key }),
      description: t("admin.deleteRecordDescription"),
      confirmLabel: t("common.delete"),
      destructive: true,
    });
    if (ok) removeKey.mutate(row.key);
  };

  const translateAll = () => {
    const ns = namespace ?? visibleNamespaces[0];
    if (!ns) return;
    sectionTranslate.mutate(
      { namespace: ns },
      {
        onSuccess: (result) => toast.success(t("translations.sectionTranslated", { count: result.translated })),
        onError: (error) => toast.error(errorMessage(error)),
      },
    );
  };

  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">{t("nav.translations")}</h1>
          <p className="text-sm text-muted-foreground">{t("translations.workbenchSubtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/settings">
              <Settings2 className="h-4 w-4" /> {t("admin.configureTranslation")}
            </Link>
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setKeyForm({ key: "", namespace: "nav", description: "" });
              setKeyDialog({ mode: "create" });
            }}
          >
            <KeyRound className="h-4 w-4" />
            {t("admin.addKey")}
          </Button>
          <TranslateSectionButton
            onTranslate={translateAll}
            count={rows.length}
            busy={sectionTranslate.isPending}
          />
          <Button variant="outline" onClick={() => publish.mutate()} disabled={publish.isPending}>
            <Send className="h-4 w-4" />
            {t("translations.publish")}
          </Button>
        </div>
      </div>

      <Dialog open={keyDialog !== null} onOpenChange={(o) => !o && setKeyDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {keyDialog?.mode === "edit" ? t("common.edit") : t("admin.addKey")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("translations.namespace")}</Label>
              <Input
                value={keyForm.namespace}
                onChange={(e) => setKeyForm({ ...keyForm, namespace: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                {keyForm.namespace}.{keyForm.key.replace(/^.*\\./, "") || "…"}
              </p>
            </div>
            <div className="space-y-2">
              <Label>{t("translations.key")}</Label>
              <Input
                value={keyForm.key}
                onChange={(e) => setKeyForm({ ...keyForm, key: e.target.value })}
                placeholder="portals"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("admin.description")}</Label>
              <Input
                value={keyForm.description}
                onChange={(e) => setKeyForm({ ...keyForm, description: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              {keyDialog?.mode === "edit" ? (
                <Button
                  variant="destructive"
                  onClick={() => removeKey.mutate(keyDialog.row.key)}
                  disabled={removeKey.isPending}
                >
                  <Trash2 className="h-4 w-4" /> {t("common.delete")}
                </Button>
              ) : null}
              <Button variant="outline" onClick={() => setKeyDialog(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={() => void saveKey()}
                disabled={!keyForm.key.trim() || !keyForm.namespace.trim()}
              >
                {t("common.save")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={q}
          onChange={(event) => setQ(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              setCommittedQ(q);
              setOffset(0);
            }
          }}
          placeholder={t("translations.searchPlaceholder")}
          className="max-w-xs"
        />
        <select
          value={namespace ?? ""}
          onChange={(event) => {
            setNamespace(event.target.value || undefined);
            setOffset(0);
          }}
          className="h-9 rounded-md border bg-background px-3 text-sm"
        >
          <option value="">{t("translations.allNamespaces")}</option>
          {visibleNamespaces.map((ns) => (
            <option key={ns} value={ns}>
              {ns}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={machineOnly} onCheckedChange={(checked) => { setMachineOnly(checked); setOffset(0); }} />
          {t("translations.machineOnly")}
        </label>
      </div>

      {ghostRecord ? (
        <AuditGhostRecord
          entityType={audit.entityType ?? "translation_key"}
          entityId={audit.recordId}
        />
      ) : null}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("translations.key")}</TableHead>
              {active.map((language) => (
                <TableHead key={language.code}>
                  {language.code}
                  {language.code === primaryCode ? ` (${t("translations.primary")})` : ""}
                </TableHead>
              ))}
              <TableHead className="w-24">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow>
                <TableCell colSpan={active.length + 2}>
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={active.length + 2} className="py-8">
                  <EmptyState title={t("translations.empty")} />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.key_id}
                  className={
                    String(row.key_id) === highlight
                      ? "audit-record-highlight cursor-pointer"
                      : "cursor-pointer"
                  }
                  onDoubleClick={() => {
                    setKeyForm({
                      key: row.key,
                      namespace: row.namespace,
                      description: row.description ?? "",
                    });
                    setKeyDialog({ mode: "edit", row });
                  }}
                >
                  <TableCell className="align-top">
                    <button
                      type="button"
                      className="group flex items-center gap-1 font-mono text-xs text-primary hover:underline"
                      onClick={() => {
                        setKeyForm({
                          key: row.key,
                          namespace: row.namespace,
                          description: row.description ?? "",
                        });
                        setKeyDialog({ mode: "edit", row });
                      }}
                    >
                      {row.key}
                      <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                    </button>
                    <Badge variant="secondary" className="mt-1">
                      {row.namespace}
                    </Badge>
                  </TableCell>
                  {active.map((language) => (
                    <TableCell key={language.code} className="align-top">
                      <ValueCell
                        row={row}
                        locale={language.code}
                        primary={language.code === primaryCode}
                        primaryCode={primaryCode}
                      />
                    </TableCell>
                  ))}
                  <TableCell className="align-top">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title={t("common.edit")}
                        aria-label={t("common.edit")}
                        onClick={() => {
                          setKeyForm({
                            key: row.key,
                            namespace: row.namespace,
                            description: row.description ?? "",
                          });
                          setKeyDialog({ mode: "edit", row });
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        title={t("common.delete")}
                        aria-label={t("common.delete")}
                        onClick={() => void confirmDeleteKey(row)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
          >
            {t("common.back")}
          </Button>
          <span>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} / {total}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset((value) => value + PAGE_SIZE)}
          >
            {t("common.next")}
          </Button>
        </div>
      ) : null}
      <div className="flex items-center gap-2">
        <Save className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{t("translations.editHint")}</span>
      </div>
    </div>
  );
}
